/**
 * m3 echo Frontend - why a booking was refused
 *
 * The refusal used to live inside `MonthlyQuotaPanel`, which made it a
 * casualty of a failed quota read: with no quota the panel does not render,
 * and the dialog was left saying "Passt nicht in das Kontingent - siehe
 * Begründung oben" with no Begründung anywhere on screen. The reason belongs
 * to the *preview*, not to the bars, so it lives here and both callers render
 * the same one.
 */

import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { BookingPreview } from '../../services/autopilots';
import { QUOTA_OVER_LIMIT_INK } from './QuotaBar';

/**
 * Two checks, two different problems: no room left in the part-month that is
 * running, versus a full-month price the limit cannot carry at all. The
 * strings state which one failed and by how much; they prescribe no remedy,
 * and the wording is fixed by the spec. Empty whenever nothing is refused.
 *
 * The numbers are stated whether or not the server is enforcing. Only the last
 * line changes: with `enforced: false` the configuration can still be saved,
 * and saying so is the difference between an explanation and a lie about a
 * button that is not in fact disabled. Showing the overage is not the same as
 * blocking the save, and the customer is owed the first either way.
 */
export const refusalReasons = (preview?: BookingPreview | null): string[] => {
  if (!preview || preview.fits) return [];
  const reasons: string[] = [];
  if (preview.initial_booking > preview.available_now) {
    reasons.push(`Dieser Monat hat nur noch ${preview.available_now} Prompts frei.`);
  }
  if (preview.steady_state_cost > preview.monthly_limit) {
    reasons.push(
      `Ab dem Folgemonat würde diese Konfiguration ${preview.steady_state_cost} von ${preview.monthly_limit} Prompts kosten.`
    );
  }
  if (reasons.length === 0) {
    reasons.push('Diese Konfiguration passt nicht in das monatliche Kontingent.');
  }
  if (!preview.enforced) {
    reasons.push(
      'Das Monatskontingent wird derzeit nicht erzwungen - diese Konfiguration lässt sich trotzdem speichern und wird als Überschreitung erfasst.'
    );
  }
  return reasons;
};

/**
 * Hand-built rather than MUI's `Alert`, so this provably shows one red.
 * `Alert` ships its own severity palette derived from `error.main`; overriding
 * it still leaves those hexes in the emitted stylesheet, and its filled wash
 * drops this ink under the 4.5:1 body-text floor (4.28:1 at an 8% tint) where
 * white clears it at 4.80:1. This also matches the overflow line inside
 * `QuotaBar` - one status treatment, wherever a refusal is stated.
 */
export const QuotaRefusal: React.FC<{ reasons: string[] }> = ({ reasons }) => {
  if (reasons.length === 0) return null;
  return (
    <Box
      role="alert"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        p: 1.5,
        borderRadius: '4px',
        border: `1px solid ${QUOTA_OVER_LIMIT_INK}`,
        backgroundColor: '#ffffff',
      }}
    >
      <WarningAmberIcon
        sx={{ fontSize: 20, color: QUOTA_OVER_LIMIT_INK, flexShrink: 0, mt: '1px' }}
      />
      <Stack spacing={0.5}>
        {reasons.map((reason) => (
          <Typography key={reason} variant="body2" sx={{ color: QUOTA_OVER_LIMIT_INK }}>
            {reason}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
};

export default QuotaRefusal;
